import {
  IProduct,
  IProductSeo,
  productApi,
  ProductData,
  productDataTransformer,
  productSeoTransformer,
} from '@commerce-product-webcomponents/sdk';
import { Plans, subscriptionApi } from '@commerce-subscription-webcomponents/sdk';
import { Logger } from '@nextgen-web-framework/core';
import { getRequestOptions, isFeatureUnavailable } from 'app/utils/common';
import { STATUS_CODE, TENANT } from 'types/common';
import logObject from 'utils/logObject';
import { PRODUCT, SUBSCRIPTION } from 'utils/service-keys';

const logger = Logger.getInstance(logObject('[product.ts]', 'Product'));

const getProductEssentials = async (locale: string, pid: string) => {
  let product: IProduct | undefined;
  let productData: ProductData | Response | undefined;
  let productSeoData: IProductSeo | undefined;
  let productErrorCode: number | undefined;
  const isSSR = typeof window === 'undefined';
  try {
    productData = await productApi.getProductByIdOrError(
      {
        productId: pid as string,
      },
      {
        ...(isSSR ? getRequestOptions(locale as string, PRODUCT) : {}),
      },
    );
    if (!(productData instanceof Response)) {
      product = productDataTransformer(productData);
      productSeoData = productSeoTransformer(productData);
    } else {
      productErrorCode = productData.status;
    }
  } catch (err) {
    productErrorCode = STATUS_CODE.INTERNAL_SERVER_ERROR;
    logger.error('Product Api Error', err);
  }
  return {
    productData,
    product,
    productErrorCode,
    productSeoData,
  };
};

const getSubscriptionByProduct = async (locale: string, pid: string) => {
  let subscription: Plans | undefined;
  let subscriptionError = false;
  const isSSR = typeof window === 'undefined';
  try {
    subscription = await subscriptionApi.getPlanDetailsByProductId(
      {
        productId: pid as string,
      },
      {
        ...(isSSR ? getRequestOptions(locale as string, SUBSCRIPTION) : {}),
      },
    );
  } catch (error) {
    subscriptionError = true;
    logger.error('Product Page Subscription Api Error', error);
  }
  return {
    subscription,
    subscriptionError,
  };
};

const getProductPageEssentials = async (locale: string, pid: string) => {
  let subscriptionData: Plans | undefined;
  let hasSubscriptionError = false;
  const { product, productData, productErrorCode } = await getProductEssentials(locale, pid);
  if (!productErrorCode && !isFeatureUnavailable([TENANT.LATAM])) {
    const { subscription, subscriptionError } = await getSubscriptionByProduct(locale, pid);
    subscriptionData = subscription;
    hasSubscriptionError = subscriptionError;
  }
  return {
    product,
    productData,
    subscriptionData,
    productErrorCode,
    hasSubscriptionError,
  };
};

export { getProductPageEssentials, getProductEssentials, getSubscriptionByProduct };
